import { create } from 'zustand';
import { queryRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import { useAuthStore } from '../auth/auth-store';
import type { Individual } from '../types/intake';
import type { QueryOptions } from '../api/types';

const PAGE_SIZE = 100;

// Stub type — property items not yet implemented in V2
export interface PropertyItem {
  id: number; incidentId: number | null; description: string; category: string;
  status: string; storageLocation: string; quantity: string; ownerId: string;
  itemCode: string; notes: string; photoData?: string; datecreated?: string;
  // Extended fields used by Property.tsx
  receivedBy?: string; individualId?: string; itemType?: string;
  serialNumber?: string; estimatedValue?: string;
}

const F = FIELDS.individuals;

function mapIndividual(r: Record<string, unknown>): Individual {
  const dateOfBirth = (r[F.dateOfBirth] as string) || '';
  const emergencyContactName = (r[F.emergencyContactName] as string) || '';
  const emergencyContactPhone = (r[F.emergencyContactPhone] as string) || '';
  const registeredAt = (r[F.registeredAt] as string) || '';
  return {
    id: r.record_id as number, incidentId: (r[F.incidentId] as number) || null,
    firstName: (r[F.firstName] as string) || '', lastName: (r[F.lastName] as string) || '',
    dateOfBirth, gender: (r[F.gender] as string) || '',
    phone: (r[F.phone] as string) || '', email: (r[F.email] as string) || '',
    address: (r[F.address] as string) || '', city: (r[F.city] as string) || '',
    state: (r[F.state] as string) || '', zip: (r[F.zip] as string) || '',
    triage: (r[F.triage] as string) || 'green', status: (r[F.status] as string) || 'active',
    emergencyContactName, emergencyContactPhone,
    medicalNeeds: (r[F.medicalNeeds] as string) || '', registeredBy: (r[F.registeredBy] as string) || '',
    registeredAt, dischargedAt: (r[F.dischargedAt] as string) || '',
    notes: (r[F.notes] as string) || '',
    // V1 compat aliases
    dob: dateOfBirth,
    tagCode: (r[F.tagCode] as string) || '',
    intakeMethod: '',
    checkedInAt: registeredAt,
    medications: '',
    emergencyContact: emergencyContactName,
    contactPhone: emergencyContactPhone,
    photoUrl: (r[F.photoUrl] as string) || '',
    datecreated: (r.created_at as string) || '',
  };
}

function toPayload(data: Partial<Individual>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.incidentId !== undefined) p[F.incidentId] = data.incidentId;
  if (data.firstName !== undefined) p[F.firstName] = data.firstName;
  if (data.lastName !== undefined) p[F.lastName] = data.lastName;
  if (data.dateOfBirth !== undefined) p[F.dateOfBirth] = data.dateOfBirth;
  if ((data as any).dob !== undefined) p[F.dateOfBirth] = (data as any).dob;
  if (data.gender !== undefined) p[F.gender] = data.gender;
  if (data.phone !== undefined) p[F.phone] = data.phone;
  if (data.email !== undefined) p[F.email] = data.email;
  if (data.address !== undefined) p[F.address] = data.address;
  if (data.city !== undefined) p[F.city] = data.city;
  if (data.state !== undefined) p[F.state] = data.state;
  if (data.zip !== undefined) p[F.zip] = data.zip;
  if (data.triage !== undefined) p[F.triage] = data.triage;
  if (data.status !== undefined) p[F.status] = data.status;
  if (data.emergencyContactName !== undefined) p[F.emergencyContactName] = data.emergencyContactName;
  if (data.emergencyContactPhone !== undefined) p[F.emergencyContactPhone] = data.emergencyContactPhone;
  // V1 compat aliases
  if ((data as any).emergencyContact !== undefined) p[F.emergencyContactName] = (data as any).emergencyContact;
  if ((data as any).contactPhone !== undefined) p[F.emergencyContactPhone] = (data as any).contactPhone;
  if (data.medicalNeeds !== undefined) p[F.medicalNeeds] = data.medicalNeeds;
  if (data.registeredBy !== undefined) p[F.registeredBy] = data.registeredBy;
  if (data.registeredAt !== undefined) p[F.registeredAt] = data.registeredAt;
  if (data.dischargedAt !== undefined) p[F.dischargedAt] = data.dischargedAt;
  if (data.notes !== undefined) p[F.notes] = data.notes;
  if ((data as any).tagCode !== undefined) p[F.tagCode] = (data as any).tagCode;
  if ((data as any).photoUrl !== undefined) p[F.photoUrl] = (data as any).photoUrl;
  return p;
}

interface IntakeState {
  individuals: Individual[]; total: number; loading: boolean; page: number;
  fetch: (opts?: Partial<QueryOptions>) => Promise<void>;
  setPage: (p: number) => void;
  create: (data: Partial<Individual>) => Promise<number | null>;
  update: (id: number, data: Partial<Individual>) => Promise<void>;
  discharge: (id: number) => Promise<void>;
  deleteIndividual: (id: number) => Promise<void>;

  // V1 compat aliases
  loadIndividuals: (incidentId?: string) => Promise<void>;
  createIndividual: (data: any) => Promise<number | null>;
  updateIndividual: (id: any, data: any) => Promise<void>;
  discharges: Individual[];
  loadDischarges: (incidentId?: string) => Promise<void>;
  createDischarge: (data: any) => Promise<number>;
  addTriageEntry: (data: any) => Promise<void>;
  propertyItems: PropertyItem[];
  loadPropertyItems: (individualId?: string | number) => Promise<void>;
  createPropertyItem: (data: any) => Promise<number | null>;
  updatePropertyItem: (id: any, data: any) => Promise<void>;
  deletePropertyItem: (id: any) => Promise<void>;
}

export const useIntakeStore = create<IntakeState>((set, get) => ({
  individuals: [], total: 0, loading: false, page: 1,
  fetch: async (opts) => {
    set({ loading: true });
    try {
      const { page } = get();
      const inc = useAuthStore.getState().activeIncident;
      const incFilter = inc ? [{ field: F.incidentId, operator: 'eq' as const, value: inc.id }] : [];
      const existingFilters = opts?.filters || [];
      const res = await queryRecords<Record<string, unknown>>(TABLES.individuals, {
        limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE,
        sort: { field: 'created_at', direction: 'desc' }, ...opts,
        filters: [...incFilter, ...existingFilters],
      });
      set({ individuals: res.records.map(mapIndividual), total: res.total });
    } catch (e) { console.error('[Intake] fetch', e); }
    set({ loading: false });
  },
  setPage: (p) => { set({ page: p }); get().fetch(); },
  create: async (data) => { try { const r = await createRecord<Record<string, unknown>>(TABLES.individuals, toPayload(data)); await get().fetch(); return (r as any).record_id; } catch (e) { console.error('[Intake]', e); return null; } },
  update: async (id, data) => { try { await updateRecord(TABLES.individuals, id, toPayload(data)); await get().fetch(); } catch (e) { console.error('[Intake]', e); } },
  discharge: async (id) => { try { await updateRecord(TABLES.individuals, id, { [F.status]: 'discharged', [F.dischargedAt]: new Date().toISOString() }); await get().fetch(); } catch (e) { console.error('[Intake]', e); } },
  deleteIndividual: async (id) => { try { await deleteRecord(TABLES.individuals, id); await get().fetch(); } catch (e) { console.error('[Intake]', e); } },

  // V1 compat aliases
  loadIndividuals: async () => get().fetch(),
  createIndividual: async (data: any) => get().create(data),
  updateIndividual: async (id: any, data: any) => get().update(id, data),
  discharges: [] as any[],
  loadDischarges: async () => {
    try {
      const inc = useAuthStore.getState().activeIncident;
      const incFilter = inc ? [{ field: F.incidentId, operator: 'eq' as const, value: inc.id }] : [];
      const res = await queryRecords<Record<string, unknown>>(TABLES.individuals, {
        limit: PAGE_SIZE,
        sort: { field: 'created_at', direction: 'desc' },
        filters: [...incFilter, { field: F.status, operator: 'eq' as const, value: 'discharged' }],
      });
      set({ discharges: res.records.map(mapIndividual) });
    } catch (e) { console.error('[Intake] loadDischarges', e); }
  },
  createDischarge: async (data: any) => {
    try {
      const id = data.individualId || data.id;
      if (id) {
        await updateRecord(TABLES.individuals, id, {
          [F.status]: 'discharged',
          [F.dischargedAt]: data.dischargedAt || new Date().toISOString(),
        });
        await get().fetch();
        return id;
      }
      return 0;
    } catch (e) { console.error('[Intake] createDischarge', e); return 0; }
  },
  addTriageEntry: async (_data: any) => {},
  propertyItems: [] as PropertyItem[],
  loadPropertyItems: async (incidentId?: string | number) => {
    try {
      const FP = FIELDS.propertyItems;
      const inc = incidentId ?? useAuthStore.getState().activeIncident?.id;
      const filters: { field: string; operator: 'eq'; value: unknown }[] = [];
      if (inc != null) filters.push({ field: FP.incidentId, operator: 'eq', value: String(inc) });
      const r = await queryRecords<any>(TABLES.propertyItems, {
        filters,
        limit: PAGE_SIZE,
        sort: { field: 'created_at', direction: 'desc' },
      });
      set({ propertyItems: r.records.map((row: any) => ({
        id: row.record_id, incidentId: row[FP.incidentId] ? Number(row[FP.incidentId]) : null,
        description: row[FP.description] || '', itemType: row[FP.itemType] || '',
        category: row[FP.itemType] || '', serialNumber: row[FP.serialNumber] || '',
        estimatedValue: row[FP.estimatedValue] || '', status: row[FP.status] || '',
        notes: row[FP.notes] || '', storageLocation: '', quantity: '', ownerId: '',
        itemCode: '', photoData: row[FP.photo] || '',
        individualId: row[FP.individualId] ? String(row[FP.individualId]) : '',
        receivedBy: '',
      })) });
    } catch (e) { console.error('[Intake]', e); }
  },
  createPropertyItem: async (data: any) => {
    try {
      const FP = FIELDS.propertyItems;
      const inc = data.incidentId ?? useAuthStore.getState().activeIncident?.id;
      const p: Record<string,unknown> = {};
      if (inc) p[FP.incidentId] = String(inc);
      if (data.individualId) p[FP.individualId] = data.individualId;
      if (data.itemType || data.category) p[FP.itemType] = data.itemType || data.category;
      if (data.description) p[FP.description] = data.description;
      if (data.serialNumber) p[FP.serialNumber] = data.serialNumber;
      if (data.estimatedValue) p[FP.estimatedValue] = data.estimatedValue;
      if (data.status) p[FP.status] = data.status;
      if (data.notes) p[FP.notes] = data.notes;
      if (data.photoData) p[FP.photo] = data.photoData;
      const r = await createRecord<Record<string, unknown>>(TABLES.propertyItems, p);
      await get().loadPropertyItems();
      return (r.record_id ?? r.id) as number;
    } catch (e) { console.error('[Intake]', e); return null; }
  },
  updatePropertyItem: async (id: any, data: any) => {
    try {
      const FP = FIELDS.propertyItems;
      const p: Record<string,unknown> = {};
      if (data.description !== undefined) p[FP.description] = data.description;
      if (data.status !== undefined) p[FP.status] = data.status;
      if (data.notes !== undefined) p[FP.notes] = data.notes;
      await updateRecord(TABLES.propertyItems, id, p);
      await get().loadPropertyItems();
    } catch (e) { console.error('[Intake]', e); }
  },
  deletePropertyItem: async (id: any) => {
    try { await deleteRecord(TABLES.propertyItems, id); await get().loadPropertyItems(); } catch (e) { console.error('[Intake]', e); }
  },
}));
